import {
  MediaObject,
  IconUser,
  Text,
  DropdownItem,
  Dropdown,
  DropdownItemProps,
  IconChecked,
} from '@treatwell/ui';
import clsx from 'clsx';
import {memo} from 'react';
import {ANY_EMPLOYEE} from '../../store/slices/constants';
import styles from './EmployeeSelect.module.css';

type EmployeeSelectDropdownItemProps = Pick<
  DropdownItemProps,
  'isSelected' | 'isHighlighted' | 'isDropdownOption'
>;

export type EmployeeItemProps = {
  id: number | typeof ANY_EMPLOYEE;
  employeeName: string;
  avatarUrl?: string;
  isDropdownOpen?: boolean;
} & EmployeeSelectDropdownItemProps;

const EmployeeItem = memo(
  ({
    employeeName,
    avatarUrl,
    isDropdownOpen,
    isDropdownOption,
    isHighlighted,
    isSelected,
  }: EmployeeItemProps) => {
    const icon = avatarUrl ? (
      <img
        width="24"
        height="24"
        className={styles.avatar}
        src={avatarUrl}
        alt={`${employeeName}'s avatar`}
      />
    ) : (
      <IconUser className={styles.avatar} size={24} />
    );
    const isSelectedEmployee = !isDropdownOption && isSelected;
    const isSelectedEmployeeInMenu = isDropdownOption && isSelected;
    const isHighlightedAndNotSelectedInMenu =
      isDropdownOption && isHighlighted && !isSelected;
    const isNotHighlightedAndNotSelectedInMenu =
      isDropdownOption && !isHighlighted && !isSelected;
    return (
      <div
        className={clsx({
          [styles.item]: isDropdownOption,
        })}
      >
        {isSelectedEmployee && (
          <MediaObject align="center">
            {icon}
            <Text
              as="p"
              type="body"
              className={clsx(styles.text, {
                [styles.selected]: isDropdownOpen,
                [styles.highlighted]: isHighlighted,
              })}
            >
              {employeeName}
            </Text>
          </MediaObject>
        )}
        {isSelectedEmployeeInMenu && (
          <MediaObject align="center" justify="between" reverse>
            <IconChecked className={styles.checked} size={24} />
            <Text
              as="p"
              type="body"
              className={clsx(styles.text, {
                [styles.highlighted]: isHighlighted,
              })}
            >
              {employeeName}
            </Text>
          </MediaObject>
        )}
        {isHighlightedAndNotSelectedInMenu && (
          <Text
            as="p"
            type="body"
            className={clsx(styles.text, {
              [styles.highlighted]: isHighlighted,
            })}
          >
            {employeeName}
          </Text>
        )}
        {isNotHighlightedAndNotSelectedInMenu && (
          <Text as="p" type="body">
            {employeeName}
          </Text>
        )}
      </div>
    );
  },
);

type DropdownProps = {
  employeeItems: EmployeeItemProps[];
  labelId: string;
  anyEmployeeLabel: string;
  onSelected: (value: string) => void;
  onToggle?: (collapsed: boolean) => void;
  initialSelectedEmployee?: number;
};

export const EmployeeSelect = ({
  employeeItems,
  labelId,
  anyEmployeeLabel,
  onSelected,
  onToggle,
  initialSelectedEmployee,
}: DropdownProps) => {
  const employeeDropdownItems = employeeItems.map((employeeItem) => {
    return (
      <DropdownItem key={employeeItem.id} value={`${employeeItem.id}`}>
        <EmployeeItem {...employeeItem} />
      </DropdownItem>
    );
  });
  employeeDropdownItems.unshift(
    <DropdownItem key={ANY_EMPLOYEE} value={ANY_EMPLOYEE}>
      <EmployeeItem id={ANY_EMPLOYEE} employeeName={anyEmployeeLabel} />
    </DropdownItem>,
  );

  return (
    <div className={clsx(styles.dropdown)} data-cy="EmployeeSelect">
      <Dropdown
        enableScroll
        labelId={labelId}
        onSelected={onSelected}
        onToggle={onToggle}
        initialSelected={initialSelectedEmployee?.toString()}
      >
        {employeeDropdownItems}
      </Dropdown>
    </div>
  );
};
